import {
  Button,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Textarea,
} from '@heroui/react'

export default function SuggestionModal({
  isOpen,
  onClose,
  mode = 'add', // 'add', 'edit', 'create'
  suggestionData,
  setSuggestionData,
  onSave,
  isLoading = false,
  selectedDocument,
}) {
  const getTypeColor = (type) => {
    switch (type) {
      case 'SUGGESTION':
        return 'primary'
      case 'FEEDBACK':
        return 'warning'
      default:
        return 'default'
    }
  }

  const getModalTitle = () => {
    switch (mode) {
      case 'edit':
        return 'Edit Suggestion'
      case 'create':
        return 'Create Suggestion from AI Response'
      default:
        return 'Add New Suggestion'
    }
  }

  const getModalDescription = () => {
    switch (mode) {
      case 'edit':
        return 'Update this suggestion to improve your knowledge base.'
      case 'create':
        return 'Transform this AI response into a curated suggestion that will help improve future interactions with your knowledge base.'
      default:
        return 'Add a new Q&A pair to help improve your knowledge base responses.'
    }
  }

  const getButtonText = () => {
    switch (mode) {
      case 'edit':
        return isLoading ? 'Saving...' : 'Save Changes'
      case 'create':
        return isLoading ? 'Saving...' : 'Save Suggestion'
      default:
        return isLoading ? 'Adding...' : 'Add Suggestion'
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" radius="none">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <h3 className="text-xl font-semibold">{getModalTitle()}</h3>
          <p className="text-sm font-normal text-gray-600">
            {getModalDescription()}
          </p>
        </ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            {/* Info Banner - only show for create mode */}
            {mode === 'create' && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <div className="flex items-start gap-3">
                  <div className={`
                    mt-0.5 flex h-5 w-5 flex-shrink-0 items-center
                    justify-center rounded-full bg-blue-500
                  `}
                  >
                    <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="mb-1 text-sm font-medium text-blue-900">What are suggestions?</h4>
                    <p className="text-sm text-blue-700">
                      Suggestions are pre-defined Q&A pairs that help improve the accuracy and consistency of your knowledge base responses.
                      They serve as examples for better AI training and can be shown to users as quick question options.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className={`
              grid grid-cols-1 gap-6
              lg:grid-cols-2
            `}
            >
              {/* Left column - Form fields */}
              <div className="space-y-4">
                <div>
                  <label className={`
                    mb-2 block text-sm font-medium text-gray-700
                  `}
                  >
                    Suggestion Type
                  </label>
                  {mode === 'edit'
                    ? (
                        <div>
                          <Chip size="sm" color={getTypeColor(suggestionData.type)} variant="flat">
                            {suggestionData.type}
                          </Chip>
                          <p className="mt-1 text-xs text-gray-500">Type cannot be changed after creation</p>
                        </div>
                      )
                    : (
                        <Select
                          selectedKeys={[suggestionData.type]}
                          onSelectionChange={keys => setSuggestionData(prev => ({
                            ...prev,
                            type: Array.from(keys)[0] || 'SUGGESTION',
                          }))}
                          radius="none"
                          description="Choose whether this is a general suggestion or feedback for improvement"
                        >
                          <SelectItem key="SUGGESTION" description="A helpful Q&A pair for users">
                            Suggestion
                          </SelectItem>
                          <SelectItem key="FEEDBACK" description="Feedback to improve the knowledge base">
                            Feedback
                          </SelectItem>
                        </Select>
                      )}
                </div>

                <div>
                  <label className={`
                    mb-2 block text-sm font-medium text-gray-700
                  `}
                  >
                    Question
                    <span className="ml-1 text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="e.g., How do I reset my password?"
                    value={suggestionData.question}
                    onChange={e => setSuggestionData(prev => ({ ...prev, question: e.target.value }))}
                    radius="none"
                    description="The question that users might ask. Make it clear and specific."
                  />
                </div>

                <div>
                  <label className={`
                    mb-2 block text-sm font-medium text-gray-700
                  `}
                  >
                    Answer
                    <span className="ml-1 text-red-500">*</span>
                  </label>
                  <Textarea
                    placeholder="Provide a clear, helpful answer that directly addresses the question..."
                    value={suggestionData.answer}
                    onChange={e => setSuggestionData(prev => ({ ...prev, answer: e.target.value }))}
                    minRows={4}
                    maxRows={8}
                    radius="none"
                    description="The ideal response to this question. Edit the AI's answer to make it perfect for your knowledge base."
                  />
                  {mode === 'create' && (
                    <div className="mt-2 text-xs text-gray-500">
                      <strong>Tip:</strong>
                      {' '}
                      You can edit the AI's response above to make it more accurate, concise, or better formatted for your needs.
                    </div>
                  )}
                </div>
              </div>

              {/* Right column - Preview Section */}
              {(mode === 'create' || mode === 'add') && (
                <div className="lg:border-l lg:pl-6">
                  <h4 className="mb-3 text-sm font-medium text-gray-700">Preview</h4>
                  <div className="space-y-3 rounded-lg bg-gray-50 p-4">
                    <div>
                      <div className={`
                        mb-1 text-xs font-medium tracking-wide text-gray-500
                        uppercase
                      `}
                      >
                        Question
                      </div>
                      <div className="text-sm text-gray-900">
                        {suggestionData.question || 'Your question will appear here...'}
                      </div>
                    </div>
                    <div>
                      <div className={`
                        mb-1 text-xs font-medium tracking-wide text-gray-500
                        uppercase
                      `}
                      >
                        Answer
                      </div>
                      <div className="text-sm whitespace-pre-wrap text-gray-700">
                        {suggestionData.answer || 'Your answer will appear here...'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="flex justify-between">
          <div className="text-xs text-gray-500">
            This suggestion will be saved to:
            {' '}
            <span className="font-medium">{selectedDocument?.name || 'Selected Document'}</span>
          </div>
          <div className="flex gap-2">
            <Button variant="light" onPress={onClose} radius="none">
              Cancel
            </Button>
            <Button
              color="primary"
              onPress={onSave}
              isDisabled={!suggestionData?.question?.trim() || !suggestionData?.answer?.trim()}
              isLoading={isLoading}
              radius="none"
            >
              {getButtonText()}
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
